import React, { type ComponentProps } from 'react';

import * as Ariakit from '@ariakit/react';
import { CaretDown, CaretUp } from '@periodica/ui-kit/icons';
import cn from 'classnames';
import { TextFieldNew as TextField } from '../TextFieldNew';

import * as styles from './styles.module.css';

export function Provider(props: ComponentProps<typeof Ariakit.ComboboxProvider>) {
  return <Ariakit.ComboboxProvider resetValueOnHide {...props} />;
}

export function Select({
  children,
  className,
  error,
  placeholder = 'Выберите значение',
  variant = 'regular',
  ...props
}: ComponentProps<typeof Ariakit.ComboboxSelect> & {
  children?: React.ReactNode;
  className?: string;
  placeholder?: string;
  error?: boolean;
  variant?: 'regular' | 'light';
}) {
  return (
    <Ariakit.ComboboxSelect
      {...props}
      className={cn(styles.selectButton, styles[variant], error && styles.hasError, className)}
    >
      {children ? (
        <span className={styles.ellipsis}>{children}</span>
      ) : (
        <span className={cn(styles.selectPlaceholder, styles.ellipsis)}>{placeholder}</span>
      )}

      <CaretDown className={styles.iconDown} />
      <CaretUp className={styles.iconUp} />
    </Ariakit.ComboboxSelect>
  );
}

export function Popover({
  list = true,
  ...props
}: ComponentProps<typeof Ariakit.ComboboxPopover> & { list?: boolean }) {
  return (
    <Ariakit.ComboboxPopover
      gutter={8}
      sameWidth
      unmountOnHide
      {...props}
      className={cn(styles.selectPopover, list && styles.list, props.className)}
    />
  );
}

export function Option({
  className,
  render,
  ...props
}: ComponentProps<typeof Ariakit.ComboboxItem>) {
  return (
    <Ariakit.ComboboxItem className={cn(styles.selectItem, className)} render={render} {...props} />
  );
}

export function SearchField({
  placeholder,
  className,
}: {
  placeholder?: string;
  className?: string;
}) {
  const store = Ariakit.useComboboxContext();

  if (!store) {
    throw new Error('Combobox must be wrapped in ComboboxProvider');
  }

  const searchValue = Ariakit.useStoreState(store, 'inputValue');

  return (
    <div className={cn(styles.searchBlock, className)}>
      <Ariakit.ComboboxInput
        placeholder={placeholder}
        render={(props) => (
          <TextField
            {...(props as ComponentProps<typeof TextField>)}
            value={searchValue}
            onChange={(value) => store.setInputValue(value)}
          />
        )}
      />
    </div>
  );
}

export function SearchList({ className, ...props }: ComponentProps<typeof Ariakit.ComboboxList>) {
  return <Ariakit.ComboboxList className={cn(styles.list, className)} {...props} />;
}
